import * as React from "react";
import {
  CloudOff,
  Inbox,
  Loader2,
  ShieldAlert,
  TriangleAlert,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

type StatePanelProps = {
  icon: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  /**
   * `page` fills the available height for a whole-screen state; `inline` sits
   * inside a card or a table body.
   */
  size?: "page" | "inline";
  tone?: "neutral" | "danger";
} & Omit<React.HTMLAttributes<HTMLDivElement>, "title">;

/**
 * Shared frame for the feedback states so an empty list, a failure, and a
 * refusal are recognisably the same kind of message.
 */
function StatePanel({
  icon: Icon,
  title,
  description,
  actions,
  className,
  size = "inline",
  tone = "neutral",
  ...props
}: StatePanelProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 text-center",
        size === "page" ? "min-h-[60vh] px-6 py-12" : "px-6 py-10",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full",
          tone === "danger"
            ? "bg-tone-danger-surface text-tone-danger-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">{title}</p>
        {description && (
          <p className="mx-auto max-w-md text-sm text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}

export type EmptyStateProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** The action that would create the first record, when the user may. */
  actions?: React.ReactNode;
  className?: string;
  size?: "page" | "inline";
};

/** Nothing to show yet, and that is not a failure. */
export function EmptyState({
  title = "Nothing here yet",
  description,
  actions,
  className,
  size,
}: EmptyStateProps) {
  return (
    <StatePanel
      icon={Inbox}
      title={title}
      description={description}
      actions={actions}
      className={className}
      size={size}
    />
  );
}

export type LoadingStateProps = {
  /** Announced to assistive technology while the region is busy. */
  label?: string;
  className?: string;
  size?: "page" | "inline";
};

/**
 * A polite busy message. Use it when there is no meaningful skeleton to show;
 * prefer `SkeletonList` or `SkeletonTable` when the shape is already known,
 * because a skeleton that matches the result avoids a layout jump.
 */
export function LoadingState({
  label = "Loading",
  className,
  size = "inline",
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "flex items-center justify-center gap-2 text-sm text-muted-foreground",
        size === "page" ? "min-h-[60vh] px-6 py-12" : "px-6 py-10",
        className,
      )}
    >
      {/* The spin is suppressed by the global reduced-motion rule. */}
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export type ErrorStateProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Wired to a query refetch; omitted when there is nothing to retry. */
  onRetry?: () => void;
  retryLabel?: string;
  actions?: React.ReactNode;
  className?: string;
  size?: "page" | "inline";
};

/**
 * Something failed and the user can do something about it. Announced through
 * `role="alert"` because it replaces content the user asked for.
 */
export function ErrorState({
  title = "Something went wrong",
  description = "The request did not complete. Try again in a moment.",
  onRetry,
  retryLabel = "Try again",
  actions,
  className,
  size,
}: ErrorStateProps) {
  return (
    <StatePanel
      role="alert"
      icon={TriangleAlert}
      tone="danger"
      title={title}
      description={description}
      className={className}
      size={size}
      actions={
        <>
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {actions}
        </>
      }
    />
  );
}

export type DeniedStateProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  size?: "page" | "inline";
};

/**
 * The server refused this record or route. The copy never hints at what the
 * hidden content is, and the frontend never treats the absence of this state as
 * permission.
 */
export function DeniedState({
  title = "You do not have access",
  description = "Your account is not permitted to view this. Contact an administrator if you think that is wrong.",
  actions,
  className,
  size,
}: DeniedStateProps) {
  return (
    <StatePanel
      icon={ShieldAlert}
      title={title}
      description={description}
      actions={actions}
      className={className}
      size={size}
    />
  );
}

export type OfflineStateProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  size?: "page" | "inline";
};

/** The browser reports no connection, so retrying is pointless until it returns. */
export function OfflineState({
  title = "You are offline",
  description = "Changes cannot be saved until the connection returns.",
  className,
  size,
}: OfflineStateProps) {
  return (
    <StatePanel
      role="status"
      aria-live="polite"
      icon={CloudOff}
      title={title}
      description={description}
      className={className}
      size={size}
    />
  );
}

export type OfflineBannerProps = {
  online: boolean;
  className?: string;
};

/**
 * The persistent connection notice for the shell. It stays mounted so the
 * status change is announced rather than appearing as new content.
 */
export function OfflineBanner({ online, className }: OfflineBannerProps) {
  return (
    <div role="status" aria-live="polite" className={className}>
      {!online && (
        <p className="flex items-center justify-center gap-2 border-b border-tone-warning-border bg-tone-warning-surface px-4 py-2 text-xs font-medium text-tone-warning-foreground">
          <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
          You are offline. Recent changes may not be saved.
        </p>
      )}
    </div>
  );
}

export type SkeletonListProps = {
  rows?: number;
  className?: string;
  label?: string;
};

/** Placeholder rows shaped like the list they replace. */
export function SkeletonList({
  rows = 5,
  className,
  label = "Loading results",
}: SkeletonListProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={cn("flex flex-col gap-2", className)}
    >
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  );
}

export type SkeletonTableProps = {
  rows?: number;
  columns?: number;
  className?: string;
  label?: string;
};

/** Placeholder cells shaped like the table they replace. */
export function SkeletonTable({
  rows = 6,
  columns = 4,
  className,
  label = "Loading results",
}: SkeletonTableProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={cn("flex flex-col", className)}
    >
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-4 border-b px-3 py-3 last:border-b-0"
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-4 flex-1", columnIndex === 0 && "max-w-16")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
